// Filter & sort challenge cards fully client-side with URL persistence

class ChallengeFilter {

    constructor(opts = {}) {
        this.showItems = true;
        this.container = document.getElementById(opts.containerId || "challenges-container");
        if (!this.container) return;

        this.cards = Array.from(this.container.querySelectorAll(".challenge-card"));

        this.form = document.getElementById(opts.formId || "filters-form");
        if (!this.form) {
            this.selectSort = null;
            this.selectAuthor = null;
            this.selectStatus = null;
            this.init();
            return;
        }

        this.selectSort = this.form.querySelector("select[name='sort']");
        this.selectAuthor = this.form.querySelector("select[name='author']");
        this.selectStatus = this.form.querySelector("select[name='status']");

        this.resetButton = document.getElementById("reset-filters");
        this.filterToggle = document.getElementById("filter-toggle");

        this.difficultyOrder = opts.difficultyOrder || {
            'very-easy': 0,
            'easy': 1,
            'medium': 2,
            'hard': 3,
            'insane': 4,
            'expert': 5
        };

        this.init();
    }

    init() {
        this.populateAuthors();
        this.loadFromURL();

        // Wait for Alpine to fully initialize before updating the store
        setTimeout(() => {
            this.updateActiveFilterCount();
        }, 100);

        this.applyFilters();
        this.bindEvents();
    }

    /**
     * Count and update the number of active filters
     */
    updateActiveFilterCount() {
        if (!this.form) return 0;

        let count = 0;
        if (this.selectSort && this.selectSort.value) count++;
        if (this.selectAuthor && this.selectAuthor.value) count++;
        if (this.selectStatus && this.selectStatus.value) count++;

        // Update Alpine.js global store
        if (window.Alpine && window.Alpine.store) {
            const store = window.Alpine.store('challengeFilters');
            if (store && store.update) {
                store.update(count, true);
            }
        }

        return count;
    }

    loadFromURL() {
        const params = new URLSearchParams(window.location.search);
        if (!this.form) return;

        if (params.has("sort") && this.selectSort) this.selectSort.value = params.get("sort");
        if (params.has("author") && this.selectAuthor) this.selectAuthor.value = params.get("author");
        if (params.has("status") && this.selectStatus) this.selectStatus.value = params.get("status");
    }

    updateURL() {
        if (!this.form) return;

        const params = new URLSearchParams();

        if (this.selectSort && this.selectSort.value) params.set("sort", this.selectSort.value);
        if (this.selectAuthor && this.selectAuthor.value) params.set("author", this.selectAuthor.value);
        if (this.selectStatus && this.selectStatus.value) params.set("status", this.selectStatus.value);

        const qs = params.toString();
        const newURL = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
        history.replaceState({}, "", newURL);
    }

    resetFilters() {
        if (!this.form) return;

        // Reset values
        if (this.selectSort) this.selectSort.value = "";
        if (this.selectAuthor) this.selectAuthor.value = "";
        if (this.selectStatus) this.selectStatus.value = "";

        // Clear URL
        history.replaceState({}, "", window.location.pathname);

        // Update active filter count
        this.updateActiveFilterCount();

        // Apply filter reset
        this.applyFilters();
    }

    bindEvents() {
        if (this.form) {
            let t;
            this.form.addEventListener("change", () => {
                clearTimeout(t);
                t = setTimeout(() => {
                    this.updateURL();
                    this.updateActiveFilterCount();
                    this.applyFilters();
                }, 120);
            });

            this.form.addEventListener("submit", (ev) => {
                ev.preventDefault();
                this.updateURL();
                this.updateActiveFilterCount();
                this.applyFilters();
            });
        }

        if (this.resetButton) {
            this.resetButton.addEventListener("click", () => {
                this.resetFilters();
            });
        }

        // handle browser navigation
        window.addEventListener("popstate", () => {
            this.loadFromURL();
            this.updateActiveFilterCount();
            this.applyFilters();
        });
    }

    difficultyWeight(raw) {
        if (!raw && raw !== 0) return 0;
        const key = String(raw).trim().toLowerCase().replace(/\s+/g, '-');
        if (this.difficultyOrder.hasOwnProperty(key)) return this.difficultyOrder[key];
        const n = Number(raw);
        if (!Number.isNaN(n)) return n;
        return Number.MAX_SAFE_INTEGER;
    }

    valueForSort(card, sortKey) {
        switch (sortKey) {
            case 'difficulty':
                return this.difficultyWeight(card.dataset.difficulty);
            case 'points':
                return Number(card.dataset.points || 0);
            case 'validations':
                return Number(card.dataset.validations || 0);
            case 'date_online':
            case 'date':
                return new Date(card.dataset.date || card.dataset.dateOnline || card.dataset.date_online || 0).getTime() || 0;
            case 'validated':
                const v = String(card.dataset.validated || '').toLowerCase();
                return (v === '1' || v === 'true') ? 1 : 0;
            default:
                return Number(card.dataset.index || 0);
        }
    }

    populateAuthors() {
        if (!this.selectAuthor) return;

        this.selectAuthor.querySelectorAll("option:not(:first-child)").forEach(o => o.remove());
        const authors = new Set(
            this.cards.map(c => c.dataset.author || c.dataset.authorSlug || "")
                .filter(Boolean)
        );

        [...authors].sort().forEach(author => {
            const slug = this.cards.find(c => c.dataset.author === author)?.dataset.authorSlug;

            const opt = document.createElement("option");
            opt.value = slug;
            opt.textContent = author;
            this.selectAuthor.appendChild(opt);
        });
    }

    applyFilters() {
        const selectedSort = this.selectSort ? this.selectSort.value : '';
        const selectedAuthor = this.selectAuthor ? this.selectAuthor.value : '';
        const selectedStatus = this.selectStatus ? this.selectStatus.value : '';

        this.cards.forEach(card => {
            const author = String(card.dataset.authorSlug || '');
            const validatedFlag = String(card.dataset.validated || '').toLowerCase();
            const validated = (validatedFlag === '1' || validatedFlag === 'true');

            let show = true;

            if (selectedAuthor && selectedAuthor !== "" && selectedAuthor !== author) show = false;

            if (selectedStatus === "validated" && !validated) show = false;
            if (selectedStatus === "not_validated" && validated) show = false;

            card.style.display = show ? "" : "none";
        });

        // Filters
        const visibleCards = this.cards.filter(c => c.style.display !== "none");
        const cardsWithIndex = visibleCards.map((c, idx) => ({
            el: c,
            value: this.valueForSort(c, selectedSort),
            idx
        }));

        cardsWithIndex.sort((a, b) => {
            const va = a.value;
            const vb = b.value;

            if (typeof va === 'number' && typeof vb === 'number') {
                if (vb < va) return -1;
                if (vb > va) return 1;
                return a.idx - b.idx;
            }

            const sa = String(va);
            const sb = String(vb);
            const cmp = sa.localeCompare(sb, undefined, {numeric: true, sensitivity: 'base'});
            if (cmp !== 0) return cmp;
            return a.idx - b.idx;
        });

        const fragment = document.createDocumentFragment();
        cardsWithIndex.forEach(item => fragment.appendChild(item.el));
        this.container.appendChild(fragment);
    }
}

export default function challengeFilterManager() {
    return {
        activeFilters: 0,
        showFilters: false,

        init() {
            this.instance = new ChallengeFilter()
        },
        update(count, autoExpand = true) {
            this.activeFilters = count;
            if (autoExpand && count > 0) {
                this.showFilters = true;
            }
        }
    }
}
